import { Link } from 'react-router-dom'
import MarketingLayout from '../components/MarketingLayout'
import MarketingSection from '../components/MarketingSection'
import { getFormattedAddress } from '../content/publicSiteConfig'
import { useStaticPageSeo } from '@/lib/seo'

const PrivacyPolicyPage = () => {
  useStaticPageSeo('/privacy')

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <MarketingSection variant="gradient-dark" className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            {/* Static on purpose. This was built from the current year, so the date advanced
                every January whether or not the policy had actually changed. Bump it by hand
                when the text below changes. */}
            <p className="text-lg text-white/70">Last Updated: July 2026</p>
          </div>
        </div>
      </MarketingSection>

      {/* Content Section */}
      <MarketingSection variant="light">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-primary-blue/10 p-8 md:p-12">
              <div className="space-y-10 text-primary-dark/80">
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    Who We Are
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed">
                    CleanDock is operated by West Wave Creative, a DBA of Amicus Group, Inc., a California S-Corporation.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    1. Information We Collect
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    When you use CleanDock, we collect information that you provide directly to us:
                  </p>
                  <ul className="list-disc list-inside space-y-3 ml-4 text-base md:text-lg">
                    <li>
                      <strong className="text-primary-dark">Account Information:</strong> Name, email address, company name, and
                      password when you create an account
                    </li>
                    <li>
                      <strong className="text-primary-dark">Business Information:</strong> Company details, logo, contact information,
                      and business preferences
                    </li>
                    <li>
                      <strong className="text-primary-dark">Customer Data:</strong> Contact information for your clients (names, email
                      addresses, phone numbers, addresses)
                    </li>
                    <li>
                      <strong className="text-primary-dark">Phone Numbers for SMS:</strong> We collect phone numbers from users who opt in
                      to receive SMS notifications for account alerts, appointment reminders, service updates, and
                      billing notifications.
                    </li>
                    <li>
                      <strong className="text-primary-dark">Transaction Data:</strong> Quotes, invoices, bookings, and related business
                      documents
                    </li>
                    <li>
                      <strong className="text-primary-dark">Usage Data:</strong> Information about how you use our service, including
                      access times and features used
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    2. How We Use Your Information
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">We use the information we collect to:</p>
                  <ul className="list-disc list-inside space-y-3 ml-4 text-base md:text-lg">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Send transactional emails (quotes, invoices, booking confirmations)</li>
                    <li>Process and manage your account</li>
                    <li>Respond to your support requests and communications</li>
                    <li>Ensure the security and integrity of our services</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    2a. SMS Notifications
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    We collect phone numbers only from users who opt in. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out. Reply HELP for help. We do not sell phone numbers.
                  </p>
                  <p className="text-base md:text-lg leading-relaxed">
                    We only send SMS messages to users who have opted in. We do not sell or share your phone
                    number with third parties for marketing. To opt out of SMS at any time, reply STOP to any
                    message. For help, reply HELP or contact us at{' '}
                    <a
                      href="mailto:noreply@thecleandock.com"
                      className="text-primary-gold hover:text-primary-gold/80 transition-colors font-semibold"
                    >
                      noreply@thecleandock.com
                    </a>
                    . For full program details, see our{' '}
                    <Link to="/sms-consent" className="text-primary-gold hover:text-primary-gold/80 underline font-semibold">
                      SMS Notifications Consent
                    </Link>
                    .
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    3. Information Sharing and Disclosure
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    We do not sell or rent your personal information to third parties. We may share your
                    information only in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-3 ml-4 text-base md:text-lg">
                    <li>
                      <strong className="text-primary-dark">With Your Clients:</strong> When you send quotes, invoices, or booking
                      confirmations to your clients
                    </li>
                    <li>
                      <strong className="text-primary-dark">Service Providers:</strong> With third-party vendors who help us provide our
                      services (e.g., AWS for hosting, email delivery)
                    </li>
                    <li>
                      <strong className="text-primary-dark">Legal Requirements:</strong> When required by law or to protect our rights
                      and safety
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    3a. Google Calendar Integration
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    If you choose to connect your Google account, CleanDock requests permission to
                    manage a dedicated CleanDock calendar in your Google Calendar. We use this access
                    solely to keep your appointments in sync:
                  </p>
                  <ul className="list-disc list-inside space-y-3 ml-4 text-base md:text-lg">
                    <li>
                      <strong className="text-primary-dark">What we access:</strong> Permission to
                      create and manage a separate CleanDock calendar, and the events on it, within
                      your Google Calendar.
                    </li>
                    <li>
                      <strong className="text-primary-dark">How we use it:</strong> Sync is one-way.
                      CleanDock adds, updates, and removes events on the dedicated CleanDock calendar to
                      match the appointments you schedule in CleanDock. We do not read, import, or
                      store events from your other Google calendars.
                    </li>
                    <li>
                      <strong className="text-primary-dark">Storage:</strong> We store your Google
                      authorization tokens in encrypted form only to maintain the connection, and use
                      them exclusively to provide the calendar sync feature.
                    </li>
                    <li>
                      <strong className="text-primary-dark">Disconnecting:</strong> You can
                      disconnect Google Calendar at any time from your CleanDock settings, which stops
                      syncing and can remove the CleanDock calendar from your Google account.
                    </li>
                  </ul>
                  <p className="mt-4 text-base md:text-lg leading-relaxed">
                    CleanDock&apos;s use and transfer of information received from Google APIs adheres
                    to the{' '}
                    <a
                      href="https://developers.google.com/terms/api-services-user-data-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-gold hover:text-primary-gold/80 underline font-semibold"
                    >
                      Google API Services User Data Policy
                    </a>
                    , including the Limited Use requirements. We do not use Google user data for
                    advertising, and we do not sell it.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    4. Data Security
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed">
                    We implement industry-standard security measures to protect your data, including
                    encryption, secure authentication, and regular security audits. However, no method of
                    transmission over the internet is 100% secure, and we cannot guarantee absolute
                    security.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    5. Data Retention
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed">
                    We retain your account information and business data for as long as your account is
                    active or as needed to provide you services. You may request deletion of your account
                    and data at any time by contacting us.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    6. Your Rights
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">You have the right to:</p>
                  <ul className="list-disc list-inside space-y-3 ml-4 text-base md:text-lg">
                    <li>Access and review your personal information</li>
                    <li>Correct or update your information</li>
                    <li>Delete your account and associated data</li>
                    <li>Export your data</li>
                    <li>Opt out of non-essential communications</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    7. Cookies and Tracking
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    We use essential cookies to maintain your session and remember your preferences.
                  </p>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    On our public marketing pages — our home page, guides, comparisons, and signup
                    pages — we also use PostHog, a product analytics service, to understand how
                    people find and use the site. It sets a cookie so a returning browser is
                    recognised as the same visitor, and records the pages viewed, the site or
                    search that referred you, clicks on page elements, your browser and device
                    type, and an approximate location derived from your IP address. We use this
                    only to see which pages are working and where people get stuck.
                  </p>
                  <p className="text-base md:text-lg leading-relaxed">
                    Analytics does not run in the signed-in application, and it does not run on the
                    quote, invoice, and booking pages your clients see. We do not use advertising
                    cookies, we do not run ad-network or cross-site tracking pixels, and we do not
                    sell or share this data. You can block analytics with a browser privacy setting
                    or extension without affecting how the site works.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    8. Changes to This Policy
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of any
                    material changes by posting the new policy on this page and updating the "Last
                    Updated" date.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary-dark mb-4 pb-3 border-b-2 border-primary-gold/20">
                    9. Contact Us
                  </h2>
                  <p className="mb-4 text-base md:text-lg leading-relaxed">
                    If you have questions about this Privacy Policy, please contact us:
                  </p>
                  <div className="bg-primary-light border-2 border-primary-gold/20 rounded-xl p-6 mt-4">
                    <p className="mb-3 text-base md:text-lg">
                      <strong className="text-primary-dark">Email:</strong>{' '}
                      <a
                        href="mailto:noreply@thecleandock.com"
                        className="text-primary-gold hover:text-primary-gold/80 transition-colors font-semibold"
                      >
                        noreply@thecleandock.com
                      </a>
                    </p>
                    <p className="text-base md:text-lg">
                      <strong className="text-primary-dark">Address:</strong> {getFormattedAddress()}
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </MarketingSection>
    </MarketingLayout>
  )
}

export default PrivacyPolicyPage
